import type { ColumnType } from "antd/es/table"

/**
 * A saved Finance layout lists every column in the order it was saved, pinned
 * ones included, and pinned columns cannot be dragged or resized. A layout from
 * before the name and amount were pinned would otherwise leave them mid-table at
 * an old width the user has no handle to change. So the pinned-left columns are
 * moved to the front, in their declared order and at their declared width, and
 * the pinned-right ones to the end; everything else keeps the saved order.
 */
export function keepPinnedColumnsInPlace<T>(
  configured: ColumnType<T>[],
  declared: ColumnType<T>[]
): ColumnType<T>[] {
  const declaredIndex = new Map(
    declared.map((column, index) => [String(column.key), index])
  )
  const declaredByKey = new Map(
    declared.map((column) => [String(column.key), column])
  )

  const left = configured
    .filter((column) => column.fixed === "left")
    .sort(
      (a, b) =>
        (declaredIndex.get(String(a.key)) ?? 0) -
        (declaredIndex.get(String(b.key)) ?? 0)
    )
    .map((column) => ({
      ...column,
      width: declaredByKey.get(String(column.key))?.width ?? column.width
    }))
  const right = configured.filter((column) => column.fixed === "right")
  const scrolling = configured.filter(
    (column) => column.fixed !== "left" && column.fixed !== "right"
  )

  return [...left, ...scrolling, ...right]
}

// Columns an open edit row cannot work without: its save/cancel, the fields a
// line is refused without, and the party cell, whose editor clears a stale
// contractor or customer when Source/Target changes.
export const COLUMNS_AN_EDIT_NEEDS = [
  "name",
  "value",
  "item_type_display",
  "status",
  "date",
  "responsible_type_display",
  "custom_subcontractor_name",
  "actions"
] as const

export function withColumnsAnEditNeeds<T>(
  configured: ColumnType<T>[],
  declared: ColumnType<T>[]
): ColumnType<T>[] {
  const shown = new Set(configured.map((column) => String(column.key)))
  const needed = new Set<string>(COLUMNS_AN_EDIT_NEEDS)
  const missing = declared.filter(
    (column) => needed.has(String(column.key)) && !shown.has(String(column.key))
  )
  return [...configured, ...missing]
}
